import { createFileRoute, Link, useNavigate, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '../utils/toast';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { MediaForm, type MediaFormValues } from '../components/media/MediaForm';
import { useGetMediaDetail, useUpdateMedia, useDeleteMedia } from '../api/media';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { normalizeServicePriceValue } from '../utils/price';
import { FiArrowLeft, FiImage } from 'react-icons/fi';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '../components/common/UnsavedChangesModal';

export const Route = createFileRoute('/media/$mediaId/edit')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: EditMedia,
});

function EditMedia() {
  const { mediaId } = Route.useParams();
  const navigate = useNavigate();
  const { data: media, isLoading: isLoadingMedia } = useGetMediaDetail(mediaId);
  const updateMedia = useUpdateMedia();
  const deleteMedia = useDeleteMedia();
  const [isUploading, setIsUploading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { isConfirmOpen, requestExit, confirmExit, cancelExit, allowNavigation } =
    useUnsavedChangesGuard({ isDirty: isFormDirty });

  const handleSubmit = async (values: MediaFormValues) => {
    if (!values.mediaType || !media) return;

    try {
      setIsUploading(true);

      // Upload any newly added local files; keep existing remote URLs as-is
      let finalUrls: string[] = [...values.existingUrls];
      if (values.files.length > 0) {
        try {
          const uploadedUrls = (await uploadFilesToCloudinary(values.files, {
            folder: 'event_images',
            uploadPreset: 'event_preset',
          })) as unknown as string[];
          finalUrls = [...finalUrls, ...uploadedUrls];
        } catch (uploadErr) {
          console.error('Cloudinary upload error:', uploadErr);
          toast.error('Upload failed. Please try again.');
          return;
        }
      }

      if (finalUrls.length === 0) {
        toast.error('Please upload at least one media file.');
        return;
      }

      // ── Build patch with only the changed fields ──────────────────────────
      const fields: any = {};

      // media_urls: include if the set of URLs changed
      const originalUrls = media.media_urls ?? [];
      if (
        finalUrls.length !== originalUrls.length ||
        finalUrls.some((u, i) => u !== originalUrls[i])
      ) {
        fields.media_urls = finalUrls;
      }

      // media_type
      if (values.mediaType !== media.media_type) {
        fields.media_type = values.mediaType;
      }

      // caption
      if ((values.caption ?? '') !== (media.caption ?? '')) {
        fields.caption = values.caption ?? '';
      }

      // title
      if ((values.title ?? '') !== (media.title ?? '')) {
        fields.title = values.title ?? '';
      }

      // service (null = clear)
      const newServiceId = values.service?.id ?? null;
      const oldServiceId = media.service?.id ?? null;
      if (newServiceId !== oldServiceId) {
        fields.service_id = newServiceId;
      }

      // event (null = clear)
      const newEventId = values.event?.id ?? null;
      const oldEventId = media.event?.id ?? null;
      if (newEventId !== oldEventId) {
        fields.event_id = newEventId;
      }

      // collaborators: compare sorted arrays
      const newCollab = [...values.collaboratorIds].sort((a, b) => a - b);
      const oldCollab = [...(media.collaborators?.map((c: any) => c.id) ?? [])].sort(
        (a, b) => a - b,
      );
      if (JSON.stringify(newCollab) !== JSON.stringify(oldCollab)) {
        fields.collaborator_ids = values.collaboratorIds;
      }

      // platform links
      if ((values.platformLinks.website ?? '') !== (media.website ?? '')) {
        fields.website = values.platformLinks.website ?? '';
      }
      if ((values.platformLinks.spotify ?? '') !== (media.spotify ?? '')) {
        fields.spotify = values.platformLinks.spotify ?? '';
      }
      if ((values.platformLinks.soundcloud ?? '') !== (media.soundcloud ?? '')) {
        fields.soundcloud = values.platformLinks.soundcloud ?? '';
      }
      if ((values.platformLinks.appleMusic ?? '') !== (media.apple_music ?? '')) {
        fields.apple_music = values.platformLinks.appleMusic ?? '';
      }
      if ((values.platformLinks.amazonMusic ?? '') !== (media.amazon_music ?? '')) {
        fields.amazon_music = values.platformLinks.amazonMusic ?? '';
      }
      if ((values.platformLinks.youtubeMusic ?? '') !== (media.youtube_music ?? '')) {
        fields.youtube_music = values.platformLinks.youtubeMusic ?? '';
      }

      if (Object.keys(fields).length === 0) {
        toast.info('No changes detected.');
        return;
      }

      await updateMedia.mutateAsync({ mediaId, fields });
      allowNavigation();
      navigate({ to: '/media' });
    } catch (err) {
      console.error('Update media error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMedia.mutateAsync(Number(mediaId));
      allowNavigation();
      navigate({ to: '/media' });
    } catch (err) {
      console.error('Delete media error:', err);
    }
  };

  // Map API response → MediaForm initialData
  const initialData = media
    ? {
        title: media.title ?? '',
        caption: media.caption ?? '',
        existingUrls: media.media_urls ?? [],
        mediaType: media.media_type === 'audio' ? 'video' : (media.media_type as 'image' | 'video'),
        service: media.service
          ? {
              id: media.service.id,
              title: media.service.name,
              description: media.service.description,
              price: normalizeServicePriceValue(media.service.price),
            }
          : null,
        event: media.event
          ? { id: media.event.id as number, name: media.event.name as string }
          : null,
        collaboratorIds: media.collaborators?.map((c: any) => c.id) ?? [],
        initialCollaborators:
          media.collaborators?.map((c: any) => ({
            id: c.id,
            full_name: c.full_name,
            username: c.username,
            prof_pics_url: c.prof_pics_url ?? null,
          })) ?? [],
        platformLinks: {
          website: media.website ?? '',
          spotify: media.spotify ?? '',
          soundcloud: media.soundcloud ?? '',
          appleMusic: media.apple_music ?? '',
          amazonMusic: media.amazon_music ?? '',
          youtubeMusic: media.youtube_music ?? '',
        },
      }
    : undefined;

  if (isLoadingMedia) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-zinc-500">Media not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-20 md:pt-25">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <div className="flex items-center gap-2 text-zinc-500 text-[13px] sm:text-[14px] overflow-x-auto no-scrollbar whitespace-nowrap">
            <button
              onClick={() => requestExit(() => window.history.back())}
              className="hover:text-white transition-colors shrink-0 p-1 -ml-1 rounded-full hover:bg-white/5"
            >
              <FiArrowLeft size={20} />
            </button>
            <FiImage size={16} className="shrink-0" />
            <Link
              to="/media"
              onClick={(e) => {
                e.preventDefault();
                requestExit(() => navigate({ to: '/media' }));
              }}
              className="hover:text-white transition-colors font-medium shrink-0"
            >
              Media
            </Link>
            <span className="text-zinc-700 shrink-0">/</span>
            <Link
              to="/media/$mediaId"
              params={{ mediaId }}
              onClick={(e) => {
                e.preventDefault();
                requestExit(() => navigate({ to: '/media/$mediaId', params: { mediaId } }));
              }}
              className="hover:text-white transition-colors shrink-0 truncate max-w-[40vw] sm:max-w-xs"
            >
              {media.title?.trim() || 'Details'}
            </Link>
            <span className="text-zinc-700 shrink-0">/</span>
            <span className="text-zinc-400 shrink-0">Edit</span>
          </div>
        </div>

        <MediaForm
          title="Edit Media"
          submitLabel="Save Changes"
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          showDelete={true}
          isLoading={isUploading || updateMedia.isPending}
          isDeleting={deleteMedia.isPending}
          initialData={initialData}
          onDirtyChange={setIsFormDirty}
        />
      </main>

      <BottomNav />

      {isConfirmOpen && <UnsavedChangesModal onConfirm={confirmExit} onCancel={cancelExit} />}
    </div>
  );
}
